import { useMemo, useState } from 'react'
import { AlertCircle, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import {
  useGetMachineryDirectiveCompliance,
  useListStandardApplicability,
  useMachineryDirectiveRequirements,
  MACHINERY_DIRECTIVE_STANDARD_ID,
  type ManualStandardItem,
  type ManualStandardRequirementsEntry,
  type StandardApplicability,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { useListProductSources } from '@/shared/hooks/api/productSource'
import { useListUserStandards } from '@/shared/hooks/api/userStandards'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ComplianceSubPageHeader } from '../../components/ComplianceSubPageHeader'
import { ComplianceSubPageTitle } from '../../components/ComplianceSubPageTitle'
import { StandardSection } from './StandardSection'

type GroupedManualStandardEntry = {
  items: ManualStandardItem[]
}

type ManualStandardSectionEntry = GroupedManualStandardEntry & {
  standardId: string
}

function groupByStandard(
  entries: ManualStandardRequirementsEntry[]
): Map<string, GroupedManualStandardEntry> {
  const byStandard = new Map<string, GroupedManualStandardEntry>()
  for (const entry of entries) {
    const existing = byStandard.get(entry.standard)
    if (existing) {
      byStandard.set(entry.standard, {
        items: [...existing.items, ...entry.items],
      })
    } else {
      byStandard.set(entry.standard, { items: entry.items })
    }
  }
  return byStandard
}

export function ManualValidationPage() {
  const params = useParams<{
    productId: string
    machineryDirectiveComplianceId: string
  }>()
  const productId = params.productId ?? ''
  const machineryDirectiveComplianceId =
    params.machineryDirectiveComplianceId ?? ''

  const { t } = useTranslation('machineryDirectiveCompliance')
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    null
  )

  const { data: compliance } = useGetMachineryDirectiveCompliance(
    machineryDirectiveComplianceId
  )
  const isPartlyCompletedMachine = compliance?.isPartlyCompletedMachine === true
  let pageTitleKey = 'nav.manualValidation'
  if (isPartlyCompletedMachine) {
    pageTitleKey = 'nav.assemblyInstructionsValidation'
  }

  const { data: applicabilityList = [], isLoading: isLoadingApplicability } =
    useListStandardApplicability(machineryDirectiveComplianceId)

  const {
    data: machineryDirectiveRequirements = [],
    isLoading: isLoadingMachineryDirectiveRequirements,
    isError: isMachineryDirectiveRequirementsError,
    error: machineryDirectiveRequirementsError,
  } = useMachineryDirectiveRequirements(machineryDirectiveComplianceId)

  const acceptedApplicabilityWithUserStandard = useMemo(
    () =>
      applicabilityList.filter(
        (applicableStandard) =>
          applicableStandard.isAccepted === true &&
          applicableStandard.userStandardId != null
      ),
    [applicabilityList]
  )

  const acceptedUserStandardIds = useMemo(
    () =>
      acceptedApplicabilityWithUserStandard
        .map((applicableStandard) => applicableStandard.userStandardId)
        .filter((id): id is string => id != null),
    [acceptedApplicabilityWithUserStandard]
  )

  const { data: userStandardsList = [], isLoading: isLoadingUserStandards } =
    useListUserStandards({
      enabled: acceptedUserStandardIds.length > 0,
    })

  const userStandardsById = useMemo(
    () =>
      new Map(
        userStandardsList
          .filter((userStandard) =>
            acceptedUserStandardIds.includes(userStandard.id)
          )
          .map((userStandard) => [userStandard.id, userStandard])
      ),
    [userStandardsList, acceptedUserStandardIds]
  )

  const entries = useMemo((): ManualStandardRequirementsEntry[] => {
    return acceptedApplicabilityWithUserStandard.map((applicability) => {
      let userStandard
      if (applicability.userStandardId != null) {
        userStandard = userStandardsById.get(applicability.userStandardId)
      }
      const clauses = userStandard?.informationForUseRequirements?.clauses ?? []
      const items: ManualStandardItem[] = clauses
        .filter((clause) => clause.id != null)
        .map((clause) => {
          const trimmedTitle = clause.title?.trim()
          let titleText = ''
          if (trimmedTitle) {
            titleText = trimmedTitle
          }
          return {
            id: clause.id as string,
            number: clause.number,
            text: clause.fullStatement ?? clause.content ?? titleText,
          }
        })
      return {
        standard: applicability.id,
        items,
      }
    })
  }, [acceptedApplicabilityWithUserStandard, userStandardsById])

  const byStandard = useMemo(() => groupByStandard(entries), [entries])
  const standards = acceptedApplicabilityWithUserStandard
  const isLoadingUserStandardsSection =
    isLoadingApplicability ||
    (acceptedUserStandardIds.length > 0 && isLoadingUserStandards)

  const machineryDirectiveSyntheticDefinition =
    useMemo((): StandardApplicability => {
      return {
        id: MACHINERY_DIRECTIVE_STANDARD_ID,
        source: 'MACHINERY_DIRECTIVE',
        machineryDirectiveComplianceId: machineryDirectiveComplianceId,
        reference: '2006/42/EC Annex I 1.7.4',
        title: t(
          'standardsPage.manualValidationPage.machineryDirectiveCardTitle'
        ),
        type: 'DIRECTIVE',
        applicability: 'DIRECTLY_APPLICABLE',
        certainty: 'HIGH',
        explanation: '',
        isAccepted: true,
        userStandardId: null,
        inEffect: true,
        hasFile: false,
        hasSignificantHazards: false,
        hasInformationForUse: true,
        createdAt: '',
        updatedAt: '',
      }
    }, [machineryDirectiveComplianceId, t])

  const definitionsById = useMemo(() => {
    const map = new Map<string, StandardApplicability>(
      standards.map((standard) => [standard.id, standard])
    )
    map.set(
      MACHINERY_DIRECTIVE_STANDARD_ID,
      machineryDirectiveSyntheticDefinition
    )
    return map
  }, [standards, machineryDirectiveSyntheticDefinition])

  const userStandardSections = useMemo(() => {
    const sections: ManualStandardSectionEntry[] = []

    for (const [standardId, groupedEntry] of byStandard.entries()) {
      sections.push({ standardId, items: groupedEntry.items })
    }

    for (const standard of standards) {
      if (standard.isAccepted !== true) {
        continue
      }
      if (byStandard.has(standard.id)) {
        continue
      }
      sections.push({ standardId: standard.id, items: [] })
    }

    sections.sort((firstSection, secondSection) => {
      const firstHasRequirements = firstSection.items.length > 0
      const secondHasRequirements = secondSection.items.length > 0
      if (firstHasRequirements !== secondHasRequirements) {
        if (firstHasRequirements) {
          return -1
        }
        return 1
      }
      return 0
    })

    return sections
  }, [byStandard, standards])

  const { data: sources = [], isLoading: isLoadingSources } =
    useListProductSources(productId)

  const completedSources = sources.filter(
    (source) => source.ingestionStatus === 'COMPLETED'
  )

  const documentOptions = useMemo(() => {
    return completedSources.map((source) => {
      let label = source.name
      if (source.classification) {
        const classificationLabel = source.classification.replace(/_/g, ' ')
        label = `${label} (${classificationLabel})`
      }
      return { label, value: source.id }
    })
  }, [completedSources])

  const handleDocumentChange = (value: string) => {
    setSelectedDocumentId(value || null)
  }

  const hasDocuments = sources.length > 0
  const showSelectionHint =
    !isLoadingSources && hasDocuments && !selectedDocumentId
  const showEmptyHint = !isLoadingSources && !hasDocuments
  let documentPlaceholder = t(
    'standardsPage.manualValidationPage.selectDocument'
  )
  if (isLoadingSources) {
    documentPlaceholder = t(
      'standardsPage.manualValidationPage.loadingDocuments'
    )
  }

  const showMachineryDirectiveLoading = isLoadingMachineryDirectiveRequirements
  const showMachineryDirectiveError =
    !isLoadingMachineryDirectiveRequirements &&
    isMachineryDirectiveRequirementsError
  const showMachineryDirectiveSection =
    !isLoadingMachineryDirectiveRequirements &&
    !isMachineryDirectiveRequirementsError

  const showUserStandardsLoading = isLoadingUserStandardsSection
  const showNoRequirementsYet =
    !isLoadingUserStandardsSection && userStandardSections.length === 0
  const showUserStandardSections =
    !isLoadingUserStandardsSection && userStandardSections.length > 0

  return (
    <div>
      <ComplianceSubPageHeader>
        <ComplianceSubPageTitle>{t(pageTitleKey)}</ComplianceSubPageTitle>
      </ComplianceSubPageHeader>

      <div className='space-y-4'>
        <div className='space-y-2'>
          <label
            htmlFor='manual-document-selection'
            className='text-sm leading-none font-medium'
          >
            {showSelectionHint &&
              t('standardsPage.manualValidationPage.selectDocumentHint')}
          </label>
          <Select
            value={selectedDocumentId ?? ''}
            onValueChange={(value) => {
              if (value) {
                handleDocumentChange(value)
              }
            }}
            required
            items={documentOptions}
          >
            <SelectTrigger
              id='manual-document-selection'
              className='w-full max-w-md'
            >
              <SelectValue placeholder={documentPlaceholder} />
            </SelectTrigger>
            <SelectContent>
              {documentOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {showEmptyHint && (
            <p className='text-sm text-muted-foreground'>
              {t('standardsPage.manualValidationPage.noSourcesHint')}
            </p>
          )}
        </div>

        <div className='space-y-6'>
          {showMachineryDirectiveLoading && (
            <div className='flex h-48 items-center justify-center rounded-lg border border-dashed'>
              <Loader2 className='size-8 animate-spin text-muted-foreground' />
            </div>
          )}

          {showMachineryDirectiveError && (
            <Alert variant='destructive'>
              <AlertCircle className='size-4' />
              <AlertDescription>
                {machineryDirectiveRequirementsError?.message ??
                  t(
                    'standardsPage.manualValidationPage.loadRequirementsFailed'
                  )}
              </AlertDescription>
            </Alert>
          )}

          {showMachineryDirectiveSection && (
            <StandardSection
              standardDefinition={machineryDirectiveSyntheticDefinition}
              machineryDirectiveComplianceId={machineryDirectiveComplianceId}
              standardId={MACHINERY_DIRECTIVE_STANDARD_ID}
              items={machineryDirectiveRequirements}
              documentId={selectedDocumentId ?? undefined}
            />
          )}

          {showUserStandardsLoading && (
            <div className='flex justify-center py-12'>
              <Loader2 className='size-6 animate-spin text-muted-foreground' />
            </div>
          )}

          {showNoRequirementsYet && (
            <p className='py-16 text-center text-lg text-muted-foreground'>
              {t('standardsPage.manualValidationPage.noRequirementsYet')}
            </p>
          )}

          {showUserStandardSections &&
            userStandardSections.map(({ standardId, items }) => {
              const standardDefinition = definitionsById.get(standardId)
              if (!standardDefinition) {
                return null
              }
              return (
                <StandardSection
                  key={standardId}
                  standardDefinition={standardDefinition}
                  machineryDirectiveComplianceId={
                    machineryDirectiveComplianceId
                  }
                  standardId={standardId}
                  items={items}
                  documentId={selectedDocumentId ?? undefined}
                />
              )
            })}
        </div>
      </div>
    </div>
  )
}
